import moment from 'moment/min/moment.min';
import uuid from 'uuid';
import { extract, parse, stringify } from 'query-string';
import jack from 'jack-logs';
import { makeHeaders, clearSystemMessages, handleDemoRequest } from './lib';
import { add as addSystemMessage, removeByType } from '../messages/systemMessages/actions';
import { add as addNotification } from '../messages/notifications/actions';
import CustomError from '../../lib/errors/CustomError';

const API_BASE = `${__BASE_URL__}/api/v2`;

/**
 * @typedef {Object} RequestObject an object repesenting a request
 * @property {String} RequestObject.endpoint The endpoint to request (such as '/clockins')
 * @property {String} [RequestObject.method=GET] The method to use when making the request
 * @property {Object|FormData}[RequestObject.data] The data to send with the request
 */

export const actions = {
  QUEUE: 'REQUESTS_QUEUE',
  REMOVE: 'REQUESTS_REMOVE',
  PROCESS: 'REQUESTS_PROCESS',
  CLEAR: 'REQUESTS_CLEAR',
};

function safelyRetrieveObject(text) {
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

/**
 * Adds a request to the queue
 * @param {RequestObject} request A request to store
 * @returns {{type: string, value: *}} A redux action
 */
export function queueRequest(request) {
  return {
    type: actions.QUEUE,
    value: request,
  };
}

export function removeRequest(id) {
  jack.dev.log({ message: 'Removing Request', id });
  return {
    type: actions.REMOVE,
    value: id,
  };
}

export function clear() {
  return {
    type: actions.CLEAR,
  };
}

function getUUID() {
  return uuid().replace(/-/g, '');
}

function filterPhoto(obj) {
  return Object.entries(obj)
    .map(([key, value]) => {
      if (key === 'photo') {
        // redact the 'photo' key and replace with a placeholder
        return {
          [key]: value ? 'present' : 'missing',
        };
      }
      return { [key]: value };
    })
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
}

/**
 * Perform a fetch on the requested resource
 * @param {RequestObject} request
 * @returns {Promise<*>} The response from the server
 */
export function doFetch(request) {
  return async (dispatch, getState) => {
    if (window.DEMO_MODE) {
      return handleDemoRequest(request);
    }
    const headers = makeHeaders(getState(), request);
    const body = ((d) => {
      if (!request.method || request.method === 'GET') {
        return undefined;
      } else if (d instanceof FormData) {
        return d;
      } else if (d) {
        return JSON.stringify(d);
      }
      //
      // GET/HEAD requests must not have a body. so do not
      // default to null. while chrome may treat null as
      // no body. Edge does not!!!
      //
      // https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch
      //
      return undefined;
    })(request.data);
    const url = `${API_BASE}${request.endpoint}`;
    const extracted = extract(url);
    const toCallUrl = url.replace(`?${extracted}`, '');
    const parsed = parse(extracted);
    const queueId = request.id ? { queue_id: request.id } : {};
    const qsObject = {
      ...parsed,
      ...queueId,
      request_id: getUUID(),
      device_id: getState().getIn(['device', 'deviceId']),
    };
    const requestToLog = {
      url: toCallUrl,
      method: request.method || 'GET',
      request_id: qsObject.request_id,
    };
    jack.dev.log({ message: 'Request starting', request: requestToLog, createdAt: Date.now() });
    const qs = stringify(qsObject);
    const method = request.method || 'GET';
    return fetch(`${toCallUrl}?${qs}`, { headers, method, body })
      .then(async (res) => {
        const clone = res.clone();
        const data = {
          response: {
            status: res.status,
            type: res.headers.get('content-type'),
          },
        };
        // only log the request information if the request went wrong
        // only log the response body if the request went wrong
        if (!res.ok) {
          data.request = {
            body: filterPhoto(request.data),
            query: qsObject,
          };
          data.response.body = safelyRetrieveObject(await res.text());
        }
        jack.dev.log({
          message: 'Request complete',
          request_id: qsObject.request_id, // so we can link start + finish
          ...data,
          createdAt: Date.now(),
        });
        return clone;
      })
      .catch((e) => {
        // no sense in trying to send to LE if we are offline, so jack.dev.error
        jack.dev.error({ message: 'Request failed', request: requestToLog, error: e });
        return new CustomError('OfflineError', 'There is no internet connection');
      });
  };
}

function getReducer(dispatch, errorCodes) {
  return async function requestReducer(p, request) {
    return p.then(async () => {
      try {
        const res = await dispatch(doFetch(request));
        if (res.ok) {
          return dispatch(removeRequest(request.id));
        }
        jack.error({ module: 'request', message: 'Request failed with code', code: res.status });
        return errorCodes.push(res.status);
      } catch (e) {
        /*
           i kinda watch to do nothing here.
           this is the case where the request has failed.
           the request is still stored and can be tried again later.
        */
        return jack.dev.error({ module: 'request', message: '', error: e });
      }
    });
  };
}

function errorForStatus(status) {
  return (dispatch) => {
    switch (status) {
      case 401:
        return dispatch(addSystemMessage({ type: 'authError' }));
      case 402:
        return dispatch(addSystemMessage({ type: 'billingError' }));
      case 403:
        return dispatch(addSystemMessage({ type: 'forbiddenError' }));
      case 404:
        return dispatch(addSystemMessage({ type: 'deviceError' }));
      default:
        return jack.error('Unknown Network Error', { statusCode: status });
    }
  };
}

function updateClockinPendingMessage() {
  return (dispatch, getState) => {
    const requests = getState().get('requests');
    const count = requests ? requests.size : 0;
    if (count === 0) {
      dispatch(removeByType('clockinsPending')); // remove any 'clockin pending' messages
    } else {
      dispatch(addSystemMessage({
        type: 'clockinsPending',
        args: [count],
      }));
    }
  };
}

export function processRequests() {
  return async (dispatch, getState) => {
    const requests = getState().get('requests').toJS();
    if (requests.length === 0) {
      return clearSystemMessages(dispatch, removeByType);
    }

    const errorCodes = [];

    const requestReducer = getReducer(dispatch, errorCodes);
    await requests.reduce(requestReducer, Promise.resolve());
    if (errorCodes.length > 0) {
      const uniq = [...new Set(errorCodes)];
      uniq.forEach(code => dispatch(errorForStatus(code)));
    } else {
      clearSystemMessages(dispatch, removeByType);
    }

    dispatch(updateClockinPendingMessage());

    return null;
  };
}

function inSetup(state) {
  if (state && state.routing && state.routing.locationBeforeTransitions) {
    return state.routing.locationBeforeTransitions.pathname === '/setup';
  }
  return false;
}

/**
 * Perform a request via the Tanda API
 * @param {RequestObject} request The request object following the {@link RequestObject} definition
 */
export function doRequest(request) {
  const req = request;
  if (!req.method) {
    req.method = 'GET';
  }
  return async (dispatch, getState) => {
    if (/clockins/.test(req.endpoint)) {
      jack.dev.log({ action: 'queue_request', request: req, delta: getState().getIn(['device', 'delta']) });
      dispatch(queueRequest(req));
      return dispatch(processRequests());
    }

    const res = await dispatch(doFetch(req));
    if (res instanceof Error) {
      if (res.name === 'OfflineError') {
        // the device is offline
        if (inSetup(getState().toJS())) {
          dispatch(addNotification({
            type: 'error',
            body: 'Your time clock could not connect to the network.',
          }));
        } else {
          dispatch(addSystemMessage({ type: 'noNetwork' }));
        }
      }
      return null;
    }
    if (!res.ok) {
      dispatch(errorForStatus(res.status));
      return null;
    }
    // remove any existing system messages
    clearSystemMessages(dispatch, removeByType);
    dispatch(updateClockinPendingMessage());

    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return res.json();
    }
    return res.text();
  };
}

/**
 * Not 100% sure that this is the best place for these.
 * Alternatives are to put it in currentUser, or put them in as a lib,
 * because they're just helpers around the doRequest method.
 */

function getNotification(message) {
  return {
    type: 'incorrect',
    body: message,
  };
}

/**
 * Submit a leave request for a user
 * @param request {Object} The leave request object
 * @param [request.reason] {string} The reason for the request
 * @param request.user_id {int|string} The user's ID
 * @param request.hours {float|string} The amount of hours the user wants to request
 * @param request.start {Date} The ISO 8601 Date string for the requested start date.
 * @param request.finish {Date} The ISO 8601 Date string for the requested finish date.
 * @param request.leave_type {string} The type of leave being requested (usually annual)
 * @returns {Promise}
 */
export function submitLeaveRequest(request) {
  return async (dispatch) => {
    if (!request.user_id) {
      dispatch(addNotification(getNotification('User ID must be provided')));
      return false;
    }
    if (typeof request.user_id !== 'number') {
      dispatch(addNotification(getNotification('User ID must be a number')));
      return false;
    }
    if (!request.start || !request.finish) {
      dispatch(addNotification(getNotification('Provide start and end dates')));
      return false;
    }
    const start = moment(request.start);
    if (!start.isValid()) {
      dispatch(addNotification(getNotification('Requested start date is not a valid ISO 8601 Date String')));
      return false;
    }
    const finish = moment(request.finish);
    if (!finish.isValid()) {
      dispatch(addNotification(getNotification('Requested finish date is not a valid ISO 8601 Date String')));
      return false;
    }
    if (finish.isBefore(start)) {
      dispatch(addNotification(getNotification('Start date must be before finish date')));
      return false;
    }
    if (!request.leave_type) {
      dispatch(addNotification(getNotification('Select a leave type')));
      return false;
    }
    if (request.leave_type === 'Public Holiday (not worked)' && !start.isSame(finish)) {
      dispatch(addNotification(getNotification('Public holiday leave must have the same start and finish dates')));
      return false;
    }
    if (request.hours === '' || Number.isNaN(request.hours) || typeof request.hours !== 'number') {
      dispatch(addNotification(getNotification('Enter the number of hours of leave')));
      return false;
    }
    const hours = parseFloat(request.hours);
    if (hours < 1) {
      dispatch(addNotification(getNotification('Make a request with at least one hour')));
      return false;
    }
    // TODO: StatsD
    const data = {
      hours,
      user_id: request.user_id,
      start: start.format('YYYY-MM-DD'),
      finish: finish.format('YYYY-MM-DD'),
      leave_type: request.leave_type,
      reason: request.reason,
      status: 'pending',
    };

    return dispatch(doRequest({
      data,
      endpoint: '/leave?send_notification=true',
      method: 'POST',
    }));
  };
}

export function prepareLeavePage(userId) {
  return async (dispatch) => {
    if (!userId) {
      throw new Error('User ID must be provided');
    }

    if (Number.isNaN(parseInt(userId, 10))) {
      throw new Error('User ID must be an int');
    }

    const leaveBalancesRaw = await dispatch(doRequest({
      endpoint: `/leave_balances?user_ids=${userId}`,
    }));
    const leaveBalances = leaveBalancesRaw.reduce((balances, balance) => ({
      ...balances,
      [balance.leave_type]: balance.hours,
    }), {});
    const leaveTypesRaw = await dispatch(doRequest({ endpoint: `/leave/types_for/${userId}` }));
    return leaveTypesRaw.reduce((types, type) => ({
      ...types,
      [type]: leaveBalances[type] || null,
    }), {});
  };
}

export function prepareUnavailRequest(data) {
  let start;
  let finish;

  if (data.allDay) {
    start = moment(data.startDate).unix();
    finish = moment(data.finishDate).unix();
  } else { // if these have no time parts, they will === NaN (which is falsey)
    start = moment(`${data.startDate}T${data.startTime}`).unix();
    finish = moment(`${data.finishDate}T${data.finishTime}`).unix();
  }

  // core request data
  const requestObj = {
    user_id: data.user_id,
    start,
    finish,
    title: data.title,
    repeating: data.repeating,
  };

  // if repeating add repeating_info
  if (data.repeating) {
    requestObj.repeating_info = {
      interval: data.interval,
      occurrences: data.occurrences,
    };
  }

  return requestObj;
}

/**
 * Create an unavailability request for an employee
 * For repeating, occurrences is the number, interval is the time.
 * For example {interval: 'week', occurrences: 3} would be
 * 'Repeat this every week for the next 3 weeks'.
 * @param requestObj {object} The unavailability object to be passed to the server
 * @param requestObj.user_id {string|int} The requestUser's user id
 * @param requestObj.title {string} The reason for the request
 * @param requestObj.start {Date} The unix timestamp for the start of the request
 * @param requestObj.finish {Date} The unix timestamp for the end of the request
 * @param [requestObj.repeating=false] {Boolean} Should the request be repeated
 * @param requestObj.repeating_info {Object} This must be present if
 *  requestObj.repeating is `true`
 * @param [requestObj.repeating_info.interval] {String} One of ['day', 'week', 'month', 'year']
 * @param [requestObj.repeating_info.occurrences] {int} how many times to repeat
 * @returns {Promise} (Object) Resolves with object containing the server-generated unavailability
 *  object
 */
export function submitUnavailabilityRequest(requestObj) {
  return async (dispatch, getState) => {
    const request = prepareUnavailRequest(requestObj);

    const minDaysNotice = moment(getState().getIn([ // need to check if they gave enough notice
      'settings',
      'organisation',
      'unavailability_minimum_date',
    ]));

    if (typeof request.user_id !== 'number') {
      dispatch(addNotification(getNotification('User ID must be present and a number')));
      return false;
    }
    if (!request.start || !request.finish) {
      dispatch(addNotification(getNotification('Please provide start and end dates')));
      return false;
    }
    const start = moment.unix(request.start);
    if (!start.isValid()) {
      dispatch(addNotification(getNotification('Requested start date is not a valid ISO 8601 Date String')));
      return false;
    }
    if (start.isBefore(minDaysNotice)) {
      dispatch(addNotification(getNotification(`
The minimum date you can apply for unavailability is
${minDaysNotice.format('Do [of] MMMM')}.
`)));
      return false;
    }
    const finish = moment.unix(request.finish);
    if (!finish.isValid()) {
      dispatch(addNotification(getNotification('Requested finish date is not a valid ISO 8601 Date String')));
      return false;
    }
    if (start.isBefore(moment().startOf('day'))) {
      dispatch(addNotification(getNotification('Start date must not be before today')));
      return false;
    }
    if (finish.startOf('day').isBefore(start.startOf('day'))) {
      dispatch(addNotification(getNotification('Start date must be before finish date')));
      return false;
    }
    if (request.start > request.finish) {
      dispatch(addNotification(getNotification('Start time must be before finish time')));
      return false;
    }
    if (!request.title) {
      dispatch(addNotification(getNotification('Please make sure to provide a reason for your unavailability')));
      return false;
    }

    const data = {
      user_id: request.user_id,
      start: request.start,
      finish: request.finish,
      title: request.title,
    };

    data.repeating = !!request.repeating;

    if (data.repeating) {
      if (!request.repeating_info) {
        dispatch(addNotification(getNotification('If repeating is set, you must provide a `repeating_info` object')));
        return false;
      }
      if (!request.repeating_info.interval) {
        dispatch(addNotification(getNotification('If the event is recurring, please enter a repeating interval')));
        return false;
      }
      if (!['day', 'week', 'month', 'year'].includes(request.repeating_info.interval)) {
        dispatch(addNotification(getNotification('Repeating interval must be one of [day, week, month, year]')));
        return false;
      }
      if (!request.repeating_info.occurrences) {
        dispatch(addNotification(getNotification('If repeating is set, you must provide ' +
            'an `occurrences` in the `repeating_info`')));
        return false;
      }
      data.repeating_info = request.repeating_info;
    }

    // TODO: StatsD
    return dispatch(doFetch({
      data,
      endpoint: '/unavailability',
      method: 'POST',
    }));
  };
}

export function refresh() {
  return dispatch => dispatch(processRequests());
}



// WEBPACK FOOTER //
// ./src/store/requests/actions.js